import { useMemo } from 'react'

import { DeepPartial, UnpackNestedValue } from 'react-hook-form'

import { OptionI } from '@/utils/forms/optionBuilders'

interface UseFacilitySelectAllParamsIProps {
  watch: <T = any>(fieldName: string) => T
  setValue: <T = any>(
    fieldName: string,
    value: UnpackNestedValue<DeepPartial<T>>,
    options?: { shouldValidate?: boolean; shouldDirty?: boolean }
  ) => void
  facilityOptions: OptionI[]
  fieldName?: string
}

interface UseFacilitySelectAllReturnIProps {
  areAllFacilitiesSelected: boolean
  handleSelectAllFacilities: () => void
}

/**
 * Custom hook to handle "Select All" / "Deselect All" functionality for facility selection
 *
 * @param watch - React Hook Form watch function
 * @param setValue - React Hook Form setValue function
 * @param facilityOptions - Array of available facility options
 * @param fieldName - Name of the form field (defaults to 'facilities')
 * @returns Object containing allFacilitiesSelected boolean and handleSelectAllFacilities function
 */
export const useFacilitySelectAll = ({
  watch,
  setValue,
  facilityOptions,
  fieldName = 'facilities',
}: UseFacilitySelectAllParamsIProps): UseFacilitySelectAllReturnIProps => {
  const selectedFacilities = watch(fieldName) || []

  const areAllFacilitiesSelected = useMemo(() => {
    if (facilityOptions.length === 0 || selectedFacilities.length === 0) {
      return false
    }

    const selectedIds = new Set(selectedFacilities.map((f: OptionI) => f.value))

    return facilityOptions.every((option: OptionI) =>
      selectedIds.has(option.value)
    )
  }, [facilityOptions, selectedFacilities])

  const handleSelectAllFacilities = () => {
    if (areAllFacilitiesSelected) {
      // Deselect all
      setValue(fieldName, [], {
        shouldValidate: true,
        shouldDirty: true,
      })
    } else {
      // Select all available facilities
      setValue(fieldName, facilityOptions, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }
  }

  return {
    areAllFacilitiesSelected,
    handleSelectAllFacilities,
  }
}
